/**
 * Variable Chaining Engine Service
 *
 * Analyzes response shapes of prior orchestration steps and identifies candidate fields
 * for variable chaining. Generates post-response scripts that extract key fields (tokens,
 * IDs, URLs, session data) into orchestration-scoped variables for use by subsequent steps.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.5, 1.6, 1.7
 */

/**
 * Candidate field patterns that indicate chainable response values.
 * Fields matching these patterns are likely to be needed by subsequent steps.
 */
export const CHAINING_PATTERNS = [
  /token/i,
  /id$/i,
  /key$/i,
  /url/i,
  /session/i,
  /auth/i,
  /_id$/i,
  /Id$/,
];

/**
 * Analyze response shapes of prior steps and identify chaining candidates.
 *
 * Iterates steps 1..N-1 (where N is targetStepOrder), extracts top-level fields
 * from each step's response body that match CHAINING_PATTERNS, and returns
 * ChainingCandidate objects for each match.
 *
 * @param {Array} steps - Orchestration steps with execution responses
 * @param {number} targetStepOrder - The step being added (N)
 * @returns {Array<ChainingCandidate>} Candidate fields for chaining
 *
 * @typedef {Object} ChainingCandidate
 * @property {number} sourceStep - Step order of the source step
 * @property {string} fieldPath - JSON path to the field (e.g., "data.token")
 * @property {string} variableName - Generated variable name (e.g., "step1_token")
 * @property {*} fieldValue - Sample value (if response available)
 * @property {boolean} inferred - Whether this was inferred from URL pattern (no actual response)
 */
export function identifyChainingCandidates(steps, targetStepOrder) {
  const candidates = [];

  if (!Array.isArray(steps) || targetStepOrder <= 1) {
    return candidates;
  }

  const priorSteps = steps.filter(
    (step) => (step.step_order || step.stepOrder) < targetStepOrder
  );

  for (const step of priorSteps) {
    const stepOrder = step.step_order || step.stepOrder;
    const responseBody = getResponseBody(step);

    if (responseBody && typeof responseBody === 'object') {
      // Extract top-level fields matching patterns from actual response
      const fields = extractMatchingFields(responseBody);
      for (const { fieldName, fieldPath, value } of fields) {
        candidates.push({
          sourceStep: stepOrder,
          fieldPath,
          variableName: generateVariableName(stepOrder, fieldName),
          fieldValue: value,
          inferred: false,
        });
      }
    } else {
      // No response data — use heuristic inference from URL/method
      const method = (step.method || step.request_method || '').toUpperCase();
      const url = step.url || step.request_url || '';
      const inferredFields = inferResponseFields(method, url);

      for (const { fieldName } of inferredFields) {
        candidates.push({
          sourceStep: stepOrder,
          fieldPath: fieldName,
          variableName: generateVariableName(stepOrder, fieldName),
          fieldValue: null,
          inferred: true,
        });
      }
    }
  }

  return candidates;
}

/**
 * Infer likely response fields from an API's URL pattern and method.
 * Used when prior steps have not been executed yet.
 *
 * @param {string} method - HTTP method (e.g., "POST", "GET")
 * @param {string} url - Request URL
 * @returns {Array<{fieldName: string, confidence: number}>}
 */
export function inferResponseFields(method, url) {
  const fields = [];

  if (!url) return fields;

  const normalizedUrl = url.toLowerCase();

  // Auth/login endpoints likely return token
  if (/\/auth|\/login|\/signin|\/authenticate/i.test(normalizedUrl)) {
    fields.push({ fieldName: 'token', confidence: 0.9 });
    fields.push({ fieldName: 'refreshToken', confidence: 0.6 });
    if (method === 'POST') {
      fields.push({ fieldName: 'userId', confidence: 0.7 });
      fields.push({ fieldName: 'sessionId', confidence: 0.5 });
    }
  }

  // Registration endpoints
  if (/\/register|\/signup/i.test(normalizedUrl)) {
    fields.push({ fieldName: 'userId', confidence: 0.8 });
    fields.push({ fieldName: 'token', confidence: 0.6 });
  }

  // POST to resource paths likely return an ID
  if (method === 'POST' && !(/\/auth|\/login|\/signin|\/register|\/signup/i.test(normalizedUrl))) {
    fields.push({ fieldName: 'id', confidence: 0.8 });
  }

  // OAuth endpoints
  if (/\/oauth|\/token/i.test(normalizedUrl)) {
    fields.push({ fieldName: 'accessToken', confidence: 0.9 });
    fields.push({ fieldName: 'refreshToken', confidence: 0.7 });
  }

  // API key endpoints
  if (/\/api-key|\/apikey/i.test(normalizedUrl)) {
    fields.push({ fieldName: 'apiKey', confidence: 0.9 });
  }

  // Session endpoints
  if (/\/session/i.test(normalizedUrl)) {
    fields.push({ fieldName: 'sessionId', confidence: 0.9 });
  }

  return fields;
}

/**
 * Generate a variable name following the step{N}_{fieldName} pattern.
 *
 * @param {number} stepOrder - Source step order
 * @param {string} fieldName - Response field name
 * @returns {string} Variable name (e.g., "step1_token")
 */
export function generateVariableName(stepOrder, fieldName) {
  // Sanitize field name: keep only alphanumeric and underscore, ensure starts with letter
  const sanitized = fieldName
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .replace(/^[^a-zA-Z]+/, '')
    .replace(/_+/g, '_')
    .replace(/_$/, '');

  const safeName = sanitized || 'field';
  return `step${stepOrder}_${safeName}`;
}

/**
 * Generate set_step_post_script action for extracting a field into a variable.
 *
 * @param {ChainingCandidate} candidate - The chaining candidate
 * @returns {Object} Action object with type "set_step_post_script"
 */
export function generateExtractionAction(candidate) {
  const { sourceStep, fieldPath, variableName } = candidate;

  // Build the extraction path for nested fields
  const accessPath = fieldPath
    .split('.')
    .map((segment) => `['${segment}']`)
    .join('');

  const script = [
    'const response = JSON.parse(sp.response.body);',
    `sp.variable.set('${variableName}', response${accessPath});`,
  ].join('\n');

  return {
    type: 'set_step_post_script',
    value: {
      step_order: sourceStep,
      script,
    },
    chaining_metadata: {
      source_step: sourceStep,
      target_step: sourceStep + 1,
      variable_name: variableName,
    },
  };
}

/**
 * Separate chaining actions from user-requested actions in the preview.
 * Chaining actions are identified by the presence of a `chaining_metadata` field.
 *
 * @param {Array} actions - All actions from AI response
 * @returns {{userActions: Array, chainingActions: Array}}
 */
export function separateChainingActions(actions) {
  if (!Array.isArray(actions)) {
    return { userActions: [], chainingActions: [] };
  }

  const userActions = [];
  const chainingActions = [];

  for (const action of actions) {
    if (action.chaining_metadata) {
      chainingActions.push(action);
    } else {
      userActions.push(action);
    }
  }

  return { userActions, chainingActions };
}

// --- Internal Helper Functions ---

/**
 * Extract the parsed response body from a step object.
 * Handles different shapes of step data (execution result formats).
 *
 * @param {Object} step - An orchestration step
 * @returns {Object|null} Parsed JSON response body or null
 */
function getResponseBody(step) {
  // Try various response body locations
  const rawBody =
    step.response_body ||
    step.responseBody ||
    (step.response && step.response.body) ||
    (step.execution_result && step.execution_result.response_body) ||
    null;

  if (!rawBody) return null;

  if (typeof rawBody === 'object') return rawBody;

  // Try to parse string as JSON
  try {
    return JSON.parse(rawBody);
  } catch {
    return null;
  }
}

/**
 * Extract top-level fields from a response body object that match CHAINING_PATTERNS.
 * Handles nested objects by checking one level of nesting (e.g., "data.token").
 *
 * @param {Object} body - Parsed JSON response body
 * @param {string} prefix - Field path prefix for nested objects
 * @returns {Array<{fieldName: string, fieldPath: string, value: *}>}
 */
function extractMatchingFields(body, prefix = '') {
  const matches = [];

  for (const [key, value] of Object.entries(body)) {
    const fieldPath = prefix ? `${prefix}.${key}` : key;

    if (matchesChainingPattern(key)) {
      matches.push({
        fieldName: key,
        fieldPath,
        value,
      });
    }

    // Check one level of nesting (e.g., data.token, response.userId)
    if (
      !prefix &&
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      const nested = extractMatchingFields(value, key);
      matches.push(...nested);
    }
  }

  return matches;
}

/**
 * Check if a field name matches any of the CHAINING_PATTERNS.
 *
 * @param {string} fieldName - Field name to test
 * @returns {boolean}
 */
function matchesChainingPattern(fieldName) {
  return CHAINING_PATTERNS.some((pattern) => pattern.test(fieldName));
}
